import { useCallback, useEffect, useState } from 'react';

import LogoHorizontal from '@assets/logo-horizontal.png';
import { Button } from '@components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { RadioGroup, RadioGroupItem } from '@components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { pages } from '@constants/pages';
import errorsValidation from '@errors/validation';
import formatterOnlyNumber from '@formatters/onlyNumbers';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@lib/ui/cn';
import CheckoutService from '@services/checkout/CheckoutService';
import { type ICreateCheckoutInputData } from '@services/checkout/dtos/create/InputData';
import { type IGetPostalCodeInputData } from '@services/postalCode/dtos/get/InputData';
import { type IGetPostalCodeOutputData } from '@services/postalCode/dtos/get/OutputData';
import PostalCodeService from '@services/postalCode/PostalCodeService';
import { useMutation } from '@tanstack/react-query';
import {
  Badge,
  CircleCheck,
  CircleCheckBig,
  LoaderCircle,
  ShieldCheck,
  TicketPercent,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { PatternFormat } from 'react-number-format';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { Comparative } from './Comparative';

declare const window: any;

const states = [
  { label: 'AC', value: 'AC' },
  { label: 'AL', value: 'AL' },
  { label: 'AP', value: 'AP' },
  { label: 'AM', value: 'AM' },
  { label: 'BA', value: 'BA' },
  { label: 'CE', value: 'CE' },
  { label: 'DF', value: 'DF' },
  { label: 'ES', value: 'ES' },
  { label: 'GO', value: 'GO' },
  { label: 'MA', value: 'MA' },
  { label: 'MT', value: 'MT' },
  { label: 'MS', value: 'MS' },
  { label: 'MG', value: 'MG' },
  { label: 'PA', value: 'PA' },
  { label: 'PB', value: 'PB' },
  { label: 'PR', value: 'PR' },
  { label: 'PE', value: 'PE' },
  { label: 'PI', value: 'PI' },
  { label: 'RJ', value: 'RJ' },
  { label: 'RN', value: 'RN' },
  { label: 'RS', value: 'RS' },
  { label: 'RO', value: 'RO' },
  { label: 'RR', value: 'RR' },
  { label: 'SC', value: 'SC' },
  { label: 'SP', value: 'SP' },
  { label: 'SE', value: 'SE' },
  { label: 'TO', value: 'TO' },
];

const months = [
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
  { label: '5', value: '5' },
  { label: '6', value: '6' },
  { label: '7', value: '7' },
  { label: '8', value: '8' },
  { label: '9', value: '9' },
  { label: '10', value: '10' },
  { label: '11', value: '11' },
  { label: '12', value: '12' },
];

const formSchema = z
  .object({
    name: z.string().min(1, {
      message: errorsValidation.required,
    }),
    document: z.string().min(1, {
      message: errorsValidation.required,
    }),
    postalCode: z.string().min(1, {
      message: errorsValidation.required,
    }),
    state: z.string().min(1, {
      message: errorsValidation.required,
    }),
    city: z.string().min(1, {
      message: errorsValidation.required,
    }),
    neighborhood: z.string().min(1, {
      message: errorsValidation.required,
    }),
    street: z.string().min(1, {
      message: errorsValidation.required,
    }),
    number: z.string().min(1, {
      message: errorsValidation.required,
    }),
    complement: z.string(),
    cardHolder: z.string().min(1, {
      message: errorsValidation.required,
    }),
    cardNumber: z.string().min(1, {
      message: errorsValidation.required,
    }),
    cardValidityYear: z.string().min(1, {
      message: errorsValidation.required,
    }),
    cardValidityMonty: z.string().min(1, {
      message: errorsValidation.required,
    }),
    cardCvv: z.string().min(1, {
      message: errorsValidation.required,
    }),
    installments: z.string().min(1, {
      message: errorsValidation.required,
    }),
    plan: z.string(),
    coupon: z.string(),
    hasAccount: z.boolean(),
    couponApplied: z.boolean(),
  })
  .superRefine((val, ctx) => {
    if (val.coupon && !val.couponApplied) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'O cupom não foi aplicado',
        path: ['coupon'],
      });
    }
  });

interface IPlan {
  bestSelling: boolean;
  description?: string;
  id: number;
  installments: {
    id: number;
    label: string;
    price: number;
  }[];
  label: string;
  name: string;
  price: number;
}

const plans: IPlan[] = [
  {
    id: 3,
    name: 'Mensal',
    price: 29.9,
    label: 'R$ 29,90 por mês.',
    bestSelling: false,
    installments: [{ id: 1, label: 'R$ 29,90 à vista', price: 29.9 }],
  },
  {
    id: 7,
    name: 'Anual',
    price: 249.9,
    label: 'R$ 249,90 por ano.',
    bestSelling: true,
    description: '-30% em relação ao mensal.',
    installments: [{ id: 1, label: 'R$ 249,90 à vista', price: 249.9 }],
  },
];

export default function Payment() {
  const navigate = useNavigate();
  const [hasCoupon, setHasCoupon] = useState<boolean>(false);
  const [payment, setPayment] = useState<{
    installment: string;
    price: number;
  }>({
    price: plans.find((plan) => plan.bestSelling)?.price || plans[0].price,
    installment:
      plans.find((plan) => plan.bestSelling)?.installments[0].label || '',
  });
  const [discount, setDiscount] = useState<number>(0);
  const [installments, setInstallments] = useState<IPlan['installments']>(
    plans.find((plan) => plan.bestSelling)?.installments || [],
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      document: '',
      state: '',
      neighborhood: '',
      number: '',
      postalCode: '',
      street: '',
      complement: '',
      city: '',
      cardCvv: '',
      cardHolder: '',
      cardNumber: '',
      cardValidityMonty: '',
      cardValidityYear: '',
      installments:
        plans.find((plan) => plan.bestSelling)?.installments[0].id.toString() ||
        '',
      coupon: '',
      plan:
        plans.find((plan) => plan.bestSelling)?.id.toString() ||
        plans[0].id.toString(),
      hasAccount: false,
      couponApplied: false,
    },
  });

  const { mutateAsync: mutateAsyncPostalCode } = useMutation<
    IGetPostalCodeOutputData,
    unknown,
    IGetPostalCodeInputData
  >({
    mutationFn: PostalCodeService.get,
    onSuccess: ({ city, neighborhood, state, street }) => {
      form.setValue('city', city || '', { shouldValidate: true });
      form.setValue('neighborhood', neighborhood || '', {
        shouldValidate: true,
      });
      form.setValue('state', state || '', { shouldValidate: true });
      form.setValue('street', street || '', { shouldValidate: true });
    },
  });

  const { isPending, mutateAsync } = useMutation<
    unknown,
    unknown,
    ICreateCheckoutInputData
  >({
    mutationFn: CheckoutService.create,
    onError: () => {
      toast.error('Atenção', {
        description:
          'Não foi possível realizar o pagamento. Verifique as informações e tente novamente',
      });
    },
    onSuccess: () => {
      navigate(pages.paymentSuccess, { replace: true });
    },
  });

  function generateYearsArray(startYear: number, endYear: number) {
    const years = [];
    for (let i = startYear; i <= endYear; i++) {
      years.push(i);
    }
    return years;
  }

  const handleGetPostalCode = useCallback(async () => {
    const postalCode = form.getValues('postalCode');
    await mutateAsyncPostalCode({
      postalCode: formatterOnlyNumber(postalCode),
    });
  }, [form, mutateAsyncPostalCode]);

  const handleApplyCoupon = useCallback(() => {
    if (form.getValues('coupon') === 'DESCONTAOasfasfaas asfas fas fasf') {
      toast.success('Cupom aplicado com sucesso');
      setDiscount(10);
      form.setValue('couponApplied', true, { shouldValidate: true });
      form.trigger('coupon');
    } else {
      toast.error('Cupom inválido');
      form.setValue('coupon', '');
    }
  }, [form]);

  const handleRemoveCoupon = useCallback(() => {
    form.setValue('coupon', '');
    form.setValue('couponApplied', false);
    setDiscount(0);
    setHasCoupon(false);
  }, [form]);

  const handleSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      await mutateAsync({
        card: {
          holder: {
            address: {
              postalCode: formatterOnlyNumber(values.postalCode),
              street: values.street,
              number: values.number,
              complement: values.complement || null,
              neighborhood: values.neighborhood,
              city: values.city,
              state: values.state,
            },
            document: formatterOnlyNumber(values.document),
            name: values.name,
          },
          expiration: {
            month: Number(values.cardValidityMonty),
            year: Number(values.cardValidityYear),
          },
          number: formatterOnlyNumber(values.cardNumber),
          securityCode: values.cardCvv,
        },
        installments: Number(values.installments),
        planId: Number(values.plan),
      });
    },
    [mutateAsync],
  );

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'plan' && value.plan) {
        if (value.plan === '3') {
          handleRemoveCoupon();
        }
        const currentPlan = plans.find(
          (plan) => plan.id.toString() === value.plan,
        );
        if (currentPlan) {
          setInstallments(currentPlan.installments);
          setPayment({
            price: currentPlan.installments[0].price,
            installment: currentPlan.installments[0].label,
          });
          form.setValue(
            'installments',
            currentPlan.installments[0].id.toString(),
          );
        }
      }
      if (name === 'installments' && value.installments) {
        const currentInstallment = plans
          .find((plan) => plan.id.toString() === value.plan)
          ?.installments.find(
            (item) => item.id.toString() === value.installments,
          );
        if (currentInstallment) {
          setPayment({
            price: currentInstallment.price,
            installment: currentInstallment.label,
          });
        }
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!window.fbq) {
      (function (f: any, b, e, v) {
        if (f.fbq) {
          return;
        }

        const n: any = (f.fbq = function () {
          if (n.callMethod) {
            // eslint-disable-next-line prefer-spread, prefer-rest-params
            n.callMethod.apply(n, arguments);
          } else {
            // eslint-disable-next-line prefer-rest-params
            n.queue.push(arguments);
          }
        });
        if (!f._fbq) {
          f._fbq = n;
        }
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];

        const t: any = b.createElement(e);
        t.async = !0;
        t.src = v;

        const s: any = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(
        window,
        document,
        'script',
        'https://connect.facebook.net/en_US/fbevents.js',
      );
      window.fbq('init', '937559308050764');
      window.fbq('track', 'PageView');
    }
  }, []);

  return (
    <div>
      <div className="mx-auto max-w-screen-lg md:px-12 lg:px-0">
        <h1 className="mb-6 text-center text-2xl font-normal md:text-3xl lg:my-10">
          Seja <span className="font-bold">plus</span> e torne seu dia mais
          prático e seguro!
        </h1>
        <Form {...form}>
          <form
            className="grid grid-cols-1 gap-8 lg:grid-cols-[0.75fr_1fr]"
            onSubmit={form.handleSubmit(handleSubmit)}>
            <div className="mx-auto max-w-lg">
              <div className="lg:sticky lg:top-2">
                <div className="gradient-background h-fit rounded-xl p-6 shadow-lg">
                  <img
                    alt="Logo GPMED"
                    className="mx-auto mb-2 w-36 lg:mb-6 lg:w-48"
                    src={LogoHorizontal}
                  />

                  <Comparative />

                  <div className="bg-background/30 h-px w-full" />

                  <h1 className="text-secondary my-4 text-center font-semibold tracking-wider">
                    Escolha o seu plano
                  </h1>

                  <FormField
                    control={form.control}
                    name="plan"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <RadioGroup
                            className="flex flex-col"
                            defaultValue={field.value}
                            onValueChange={field.onChange}>
                            {plans.map((plan) => (
                              <FormItem
                                className="flex items-center"
                                key={plan.id}>
                                <FormControl>
                                  <RadioGroupItem
                                    className="border-background data-[state=checked]:border-secondary [&_svg]:fill-secondary size-6 cursor-pointer border-2 transition-all duration-300 [&_svg]:size-5"
                                    value={plan.id.toString()}
                                  />
                                </FormControl>
                                <div
                                  className={cn(
                                    'relative rounded-lg border-2 border-transparent p-2',
                                    field.value === plan.id.toString() &&
                                      'border-secondary',
                                  )}>
                                  {plan.bestSelling && (
                                    <span
                                      className={cn(
                                        'text-background bg-secondary absolute right-3 top-4 rounded-full px-2 py-1 text-sm leading-none tracking-wider transition-all duration-300',
                                        field.value === plan.id.toString() &&
                                          '-top-3',
                                      )}>
                                      mais popular
                                    </span>
                                  )}
                                  <FormLabel className="text-background cursor-pointer text-xl font-bold tracking-wider lg:text-2xl">
                                    {plan.name}
                                    <FormDescription className="text-background text-base font-light tracking-wider lg:text-lg">
                                      {plan.label}
                                    </FormDescription>
                                    <FormDescription className="text-background text-base font-light tracking-wider lg:text-lg">
                                      {plan.description}
                                    </FormDescription>
                                  </FormLabel>
                                </div>
                              </FormItem>
                            ))}
                          </RadioGroup>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <p className="text-background mx-auto mt-4 text-center text-xs font-light lg:mt-8">
                    * Acesso imediato e renovado automaticamente ao final do
                    período
                  </p>
                </div>
                <div className="mx-auto mt-2 flex flex-row items-center justify-center gap-1 px-4">
                  <div className="relative w-fit">
                    <Badge className="fill-success text-success size-8" />
                    <CircleCheck className="text-background absolute right-1.5 top-1.5 size-5" />
                  </div>
                  <span className="text-center text-xs">
                    7 dias de mais praticidade e segurança ou o seu dinheiro de
                    volta!
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col space-y-4">
              <div className="bg-primary/3 rounded-lg p-4">
                <h1 className="text-lg font-medium">Dados titular do cartão</h1>
                <div className="mb-4 flex w-full flex-col gap-2 sm:flex-row">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="flex w-full flex-col self-stretch">
                        <FormLabel className="text-primary">
                          Nome{' '}
                          <span className="text-destructive leading-none">
                            *
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="document"
                    render={({ field: { ref, ...rest } }) => (
                      <FormItem className="flex w-full flex-col self-stretch sm:max-w-36">
                        <FormLabel className="text-primary">
                          CPF{' '}
                          <span className="text-destructive leading-none">
                            *
                          </span>
                        </FormLabel>

                        <FormControl>
                          <PatternFormat
                            autoComplete="off"
                            customInput={Input}
                            format="###.###.###-##"
                            getInputRef={ref}
                            mask="_"
                            {...rest}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="bg-primary/3 rounded-lg p-4">
                <h1 className="text-lg font-medium">
                  Endereço titular do cartão
                </h1>
                <div className="mb-4 flex w-full flex-row gap-2">
                  <FormField
                    control={form.control}
                    name="postalCode"
                    render={({ field: { ref, ...rest } }) => (
                      <FormItem className="flex w-32 flex-col self-stretch">
                        <FormLabel className="text-primary">
                          CEP{' '}
                          <span className="text-destructive leading-none">
                            *
                          </span>
                        </FormLabel>
                        <FormControl>
                          <PatternFormat
                            {...rest}
                            autoComplete="off"
                            customInput={Input}
                            format="##.###-###"
                            getInputRef={ref}
                            mask="_"
                            onBlur={() => {
                              rest.onBlur();
                              handleGetPostalCode();
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="street"
                    render={({ field }) => (
                      <FormItem className="flex w-full flex-col self-stretch">
                        <FormLabel className="text-primary">
                          Rua{' '}
                          <span className="text-destructive leading-none">
                            *
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="mb-4 flex w-full flex-row gap-2">
                  <FormField
                    control={form.control}
                    name="neighborhood"
                    render={({ field }) => (
                      <FormItem className="flex w-full flex-col self-stretch">
                        <FormLabel className="text-primary">
                          Bairro{' '}
                          <span className="text-destructive leading-none">
                            *
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="number"
                    render={({ field }) => (
                      <FormItem className="flex w-52 flex-col self-stretch">
                        <FormLabel className="text-primary">
                          Número{' '}
                          <span className="text-destructive leading-none">
                            *
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="complement"
                    render={({ field }) => (
                      <FormItem className="flex w-52 flex-col self-stretch">
                        <FormLabel className="text-primary">
                          Complemento{' '}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                <div className="flex w-full flex-row gap-2">
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem className="flex w-full flex-col self-stretch">
                        <FormLabel className="text-primary">
                          Cidade{' '}
                          <span className="text-destructive leading-none">
                            *
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem className="flex w-full max-w-20 flex-col self-stretch">
                        <FormLabel className="text-primary">
                          Estado{' '}
                          <span className="text-destructive leading-none">
                            *
                          </span>
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}>
                          <FormControl>
                            <SelectTrigger onBlur={field.onBlur}>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>

                          <SelectContent className="min-w-20">
                            {states.map((state) => (
                              <SelectItem key={state.value} value={state.value}>
                                {state.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="bg-primary/3 rounded-lg p-4">
                <h1 className="text-lg font-medium">Dados do cartão</h1>
                <div className="mb-4 flex w-full flex-col gap-2 sm:flex-row">
                  <FormField
                    control={form.control}
                    name="cardHolder"
                    render={({ field }) => (
                      <FormItem className="flex w-full flex-col self-stretch">
                        <FormLabel className="text-primary">
                          Nome (como escrito no cartão){' '}
                          <span className="text-destructive leading-none">
                            *
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="cardNumber"
                    render={({ field: { ref, ...rest } }) => (
                      <FormItem className="flex w-full flex-col self-stretch sm:max-w-52">
                        <FormLabel className="text-primary">
                          Número do cartão{' '}
                          <span className="text-destructive leading-none">
                            *
                          </span>
                        </FormLabel>
                        <FormControl>
                          <PatternFormat
                            autoComplete="off"
                            customInput={Input}
                            format="#### #### #### ####"
                            getInputRef={ref}
                            mask="_"
                            {...rest}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="mb-4 flex w-full flex-row gap-2">
                  <FormField
                    control={form.control}
                    name="cardValidityMonty"
                    render={({ field }) => (
                      <FormItem className="flex w-full flex-col self-stretch">
                        <div className="flex flex-row justify-between">
                          <FormLabel className="text-primary">
                            Mês{' '}
                            <span className="text-destructive leading-none">
                              *
                            </span>
                          </FormLabel>
                        </div>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}>
                          <FormControl>
                            <SelectTrigger onBlur={field.onBlur}>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>

                          <SelectContent>
                            {months.map((month) => (
                              <SelectItem key={month.value} value={month.value}>
                                {month.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="cardValidityYear"
                    render={({ field }) => (
                      <FormItem className="flex w-full flex-col self-stretch">
                        <div className="flex flex-row justify-between">
                          <FormLabel className="text-primary">
                            Ano{' '}
                            <span className="text-destructive leading-none">
                              *
                            </span>
                          </FormLabel>
                        </div>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}>
                          <FormControl>
                            <SelectTrigger onBlur={field.onBlur}>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>

                          <SelectContent>
                            {generateYearsArray(
                              new Date().getFullYear(),
                              new Date().getFullYear() + 15,
                            ).map((year) => (
                              <SelectItem
                                key={year.toString()}
                                value={year.toString()}>
                                {year.toString()}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="cardCvv"
                    render={({ field: { ref, ...rest } }) => (
                      <FormItem className="flex w-full flex-col self-stretch">
                        <FormLabel className="text-primary">
                          CVV{' '}
                          <span className="text-destructive leading-none">
                            *
                          </span>
                        </FormLabel>
                        <FormControl>
                          <PatternFormat
                            autoComplete="off"
                            customInput={Input}
                            format="###"
                            getInputRef={ref}
                            mask="_"
                            {...rest}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="mb-4 flex w-full flex-row justify-between gap-2">
                  <FormField
                    control={form.control}
                    name="installments"
                    render={({ field }) => (
                      <FormItem className="flex w-full flex-col self-stretch">
                        <FormLabel className="text-primary">
                          Quantidade parcelas
                          <span className="text-destructive leading-none">
                            *
                          </span>
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}>
                          <FormControl>
                            <SelectTrigger onBlur={field.onBlur}>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>

                          <SelectContent className="min-w-20">
                            {installments.map((state) => (
                              <SelectItem
                                key={state.id}
                                value={state.id.toString()}>
                                {state.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {form.watch('plan') !== '3' && (
                    <Button
                      className="cursor-pointer self-end"
                      disabled={hasCoupon}
                      onClick={() => setHasCoupon(true)}
                      type="button"
                      variant="outline">
                      <TicketPercent className="size-8" strokeWidth={1.5} />
                      Inserir cupom
                    </Button>
                  )}
                </div>
                {hasCoupon && (
                  <div className="flex w-full flex-row gap-2">
                    <FormField
                      control={form.control}
                      name="coupon"
                      render={({ field, fieldState }) => (
                        <FormItem className="flex w-full flex-row">
                          <div className="flex w-full flex-col gap-2 self-stretch">
                            <FormLabel className="text-primary">
                              Cupom
                            </FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </div>
                          {form.watch('couponApplied') ? (
                            <div className="mb-1 mr-4 flex items-end justify-center">
                              <CircleCheckBig className="text-success size-8" />
                            </div>
                          ) : (
                            <Button
                              className={cn(
                                'cursor-pointer',
                                fieldState.error?.message
                                  ? 'mt-2 self-center'
                                  : 'self-end',
                              )}
                              onClick={handleApplyCoupon}
                              title="Aplicar cupom"
                              type="button"
                              variant="secondary">
                              Aplicar
                            </Button>
                          )}
                          <Button
                            className={cn(
                              'cursor-pointer',
                              fieldState.error?.message
                                ? 'mt-2 self-center'
                                : 'self-end',
                            )}
                            onClick={handleRemoveCoupon}
                            title="Remover"
                            type="button"
                            variant="destructive">
                            Remover
                          </Button>
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-col items-end justify-end self-stretch px-4 lg:px-0">
                <h1 className="whitespace-nowrap text-lg font-medium">
                  <span className={cn(discount > 0 && 'line-through')}>
                    {`Total: ${new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(payment.price)}`}
                  </span>
                </h1>

                {discount > 0 && (
                  <h1 className="whitespace-nowrap text-lg font-medium">
                    {`Total: ${new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(payment.price - discount)}`}
                  </h1>
                )}

                {discount > 0 ? (
                  <h1 className="whitespace-nowrap text-sm">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(payment.price - discount)}{' '}
                    à vista
                  </h1>
                ) : (
                  <h1 className="whitespace-nowrap text-sm">
                    {payment.installment}
                  </h1>
                )}
              </div>

              <Button
                className="gradient-background-secondary text-primary mx-4 mt-4 cursor-pointer hover:opacity-80 lg:mx-0"
                disabled={isPending}
                type="submit">
                {isPending ? (
                  <LoaderCircle className="text-primary animate-spin" />
                ) : (
                  'CONFIRMAR'
                )}
              </Button>
              <div className="flex flex-row items-center justify-center gap-1">
                <ShieldCheck strokeWidth={1.5} />
                <p className="text-xs">Ambiente 100% seguro</p>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
